/**
 * Invoicing page pieces: the contract "money bar" with its data check; the payment milestones as one
 * compact list with a Group by switch (Milestone / System / Project / Client — 24 #3); and the single
 * "By system type" table that replaced the seven repeated blocks (24 #3).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { formatMoney, formatNumber, formatPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  BODY_FONT, CardFoot, CardHead, LinkButton, ProgressBar, Segmented, SfCard, STACK_CLASS, StackBar, SystemChip, Tag,
  TableScroll, UpperLabel, numClass, plural, tableClass, tdClass, thClass,
} from './SfUi';
import { moneyBar, summarizeRows } from './invoicingModel';

const aed0 = (value) => formatMoney(value, { decimals: 0 });
const n0 = (value) => formatNumber(value, { decimals: 0 });
const pct1 = (value) => formatPercent(value, { decimals: 1 });

function LegendItem({ dotClass, label, value, valueClass }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={cn('h-2 w-2 shrink-0 rounded-full', dotClass)} aria-hidden="true" />
      <span className="text-ink-3">{label}</span>
      <b className={cn('font-semibold tabular-nums text-ink', valueClass)}>{value}</b>
    </span>
  );
}

function DataCheckLine({ check }) {
  const [open, setOpen] = useState(false);
  if (!check || check.ok) return null;
  const projects = check.overScheduled;
  return (
    <div className="mt-3 flex flex-wrap items-start gap-x-2 gap-y-1 border-t border-line-2 pt-2.5 text-[12.5px] text-ink-2" data-testid="invoicing-data-check">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-blue-ink" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        {check.hasExcess ? (
          <>
            Scheduled milestones (Not Started) total <b className="font-semibold text-ink">{aed0(check.notStarted)}</b> —{' '}
            {aed0(check.scheduledExcess)} more than the contract value not invoiced yet ({n0(check.uninvoiced)}).
          </>
        ) : 'Milestones and contract values do not add up on some projects.'}
        {projects.length ? (
          <>
            {' '}{projects[0].name} schedules {n0(projects[0].scheduled)} against {projects[0].contract ? `a ${n0(projects[0].contract)}` : 'no'} contract value.
          </>
        ) : null}
      </span>
      {projects.length ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <LinkButton data-testid="invoicing-data-check-review">Review {plural(projects.length, 'project')}</LinkButton>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[380px] rounded-xl border-line bg-surface p-0 text-ink shadow-overlay">
            <div className="border-b border-line-2 px-3.5 py-3">
              <p className="font-heading text-[13.5px] font-bold">Milestones above the contract value</p>
              <p className="mt-0.5 text-[12px] text-ink-3">Invoiced + scheduled milestones vs. the project’s contract value.</p>
            </div>
            <ul className="max-h-72 divide-y divide-line-2 overflow-y-auto">
              {projects.map((project) => (
                <li key={project.id}>
                  <Link to={`/projects/${project.id}?tab=invoicing`} className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-surface-hover">
                    <SystemChip system={project.system} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-ink">{project.name}</span>
                      <span className="block text-[12px] text-ink-3">
                        {n0(project.scheduled)} scheduled · {project.contract ? `${n0(project.contract)} contract` : 'no contract value'}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12.5px] font-semibold tabular-nums text-status-red-ink">+{formatNumber(project.excess, { decimals: 0 })}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

/**
 * props: summary (summarizeRows), base (see viewBasis: contract value, a milestone's schedule, the search
 * matches' schedule or a period's invoiced amount), label, sub, restLabel ('Not invoiced yet' / 'Not started';
 * null = no third segment), ofBase (what "% collected" is of), showOfInvoiced, check (dataCheck or null).
 */
export function MoneyBarCard({ summary, base, label, sub, restLabel = 'Not invoiced yet', ofBase = 'of contract value', showOfInvoiced = true, check }) {
  const bar = moneyBar(summary, base);
  const segments = [
    { key: 'received', value: bar.received, className: STACK_CLASS.received, label: 'Received' },
    { key: 'awaiting', value: bar.awaiting, className: STACK_CLASS.awaiting, label: 'Awaiting payment' },
  ];
  if (restLabel) segments.push({ key: 'rest', value: bar.rest, className: 'bg-[color:var(--line)]', label: restLabel });
  const shares = [`Received ${pct1(bar.receivedPct)}`, `awaiting payment ${pct1(bar.awaitingPct)}`];
  if (restLabel) shares.push(`${restLabel.toLowerCase()} ${pct1(bar.restPct)}`);
  return (
    <SfCard className="px-5 py-4 sm:px-6 sm:py-5" data-testid="invoicing-money-bar">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <UpperLabel>{label}</UpperLabel>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="flex items-baseline gap-1.5 font-heading text-[28px] font-black leading-none tracking-[-0.03em] text-ink">
              <small className="text-[13px] font-bold tracking-normal text-ink-3" style={BODY_FONT}>AED</small>
              <span className="tabular-nums" data-testid="invoicing-contract-value">{n0(bar.base)}</span>
            </span>
            {sub ? <span className="text-[13px] text-ink-3">{sub}</span> : null}
          </div>
        </div>
        <div className="text-left sm:text-right">
          {bar.base > 0 ? (
            <p className="text-[15px] font-bold text-status-green-ink" data-testid="invoicing-collected-pct">{pct1(bar.receivedPct)} collected</p>
          ) : (
            <p className="text-[15px] font-bold text-ink-3" data-testid="invoicing-collected-pct">Nothing invoiced</p>
          )}
          <p className="text-[12.5px] text-ink-3" data-testid="invoicing-collected-of">
            {ofBase}{showOfInvoiced && summary.invoiced > 0 ? ` · ${pct1(bar.collectedOfInvoicedPct)} of invoiced` : ''}
          </p>
        </div>
      </div>
      <StackBar className="mb-2.5 mt-3.5 h-3.5" total={bar.base} label={shares.join(', ')} segments={segments} />
      <div className="flex flex-wrap gap-x-7 gap-y-1.5 text-[12.5px]">
        <LegendItem dotClass="bg-status-green" label="Received" value={aed0(bar.received)} />
        <LegendItem dotClass="bg-status-amber" label="Awaiting payment" value={aed0(bar.awaiting)} />
        {restLabel ? <LegendItem dotClass="bg-[color:var(--ink-4)]" label={restLabel} value={aed0(bar.rest)} /> : null}
        <span className="ml-auto text-[12px] text-ink-3" data-testid="invoicing-currency-note">All amounts in AED</span>
      </div>
      <DataCheckLine check={check} />
    </SfCard>
  );
}

/* ------------------------------------------------------------------ payment milestones (group by) */

export const MILESTONE_GROUPINGS = [
  { key: 'milestone', label: 'Milestone' },
  { key: 'system', label: 'System' },
  { key: 'project', label: 'Project' },
  { key: 'client', label: 'Client' },
];
const GROUP_LIMIT = 8;

/** The list's rows for one grouping: [{ key, label, total, received, awaiting, notStarted, held }]. */
export function milestoneGroups(summary, rows, groupBy) {
  if (groupBy === 'milestone') {
    return summary.byMilestone.map((milestone) => ({
      key: milestone.key,
      label: milestone.label,
      total: milestone.total,
      received: milestone.received,
      awaiting: milestone.awaiting,
      notStarted: milestone.notStarted,
      held: milestone.key === 'Retention' && milestone.total > 0 && milestone.received + milestone.awaiting === 0,
    }));
  }
  const keyOf = {
    system: (row) => row.systemType || 'Unassigned',
    project: (row) => row.projectName || 'Unknown project',
    client: (row) => row.client || 'No client recorded',
  }[groupBy];
  const groups = new Map();
  rows.forEach((row) => {
    const key = keyOf(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.entries()]
    .map(([key, items]) => {
      const sum = summarizeRows(items);
      return { key, label: key, total: sum.scheduled, received: sum.received, awaiting: sum.awaiting, notStarted: sum.notStarted, system: groupBy === 'system' ? key : null };
    })
    .sort((a, b) => (b.awaiting - a.awaiting) || (b.total - a.total) || a.label.localeCompare(b.label));
}

function MilestoneRow({ item }) {
  const collected = item.total > 0 ? (item.received / item.total) * 100 : 0;
  const attention = item.awaiting > 0 && item.awaiting > item.received;
  return (
    <li className="border-b border-line-2 px-4 py-2.5 last:border-b-0 sm:px-[18px]" data-testid={`milestone-${item.key}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          {item.system ? <SystemChip system={item.system} /> : <span className="truncate text-[13.5px] font-semibold text-ink">{item.label}</span>}
        </span>
        <span className="shrink-0 font-mono text-[13px] font-semibold tabular-nums text-ink">{n0(item.total)}</span>
      </div>
      <StackBar
        className="my-1.5"
        total={item.total}
        segments={[
          { key: 'received', value: item.received, className: STACK_CLASS.received, label: 'Received' },
          { key: 'awaiting', value: item.awaiting, className: STACK_CLASS.awaiting, label: 'Awaiting payment' },
          { key: 'notStarted', value: item.notStarted, className: STACK_CLASS.notStarted, label: 'Not started' },
        ]}
      />
      {item.held ? (
        <p className="flex items-center gap-1.5 text-[12px] text-ink-3"><Tag>Held</Tag> released at handover</p>
      ) : (
        <p className={cn('text-[12px]', attention ? 'font-semibold text-status-amber-ink' : 'text-ink-3')}>
          {item.total > 0 ? `${formatPercent(collected)} collected` : 'Nothing scheduled'}
          {item.awaiting > 0 ? ` · ${n0(item.awaiting)} awaiting` : ''}
        </p>
      )}
    </li>
  );
}

/** "Payment milestones": one compact list (total, received / awaiting / not started bar, % collected) + Group by. */
export function MilestoneListCard({ summary, rows, sub }) {
  const [groupBy, setGroupBy] = useState('milestone');
  const [showAll, setShowAll] = useState(false);
  const items = useMemo(() => milestoneGroups(summary, rows, groupBy), [summary, rows, groupBy]);
  const limited = groupBy !== 'milestone' && !showAll ? items.slice(0, GROUP_LIMIT) : items;
  const total = items.reduce((sum, item) => sum + item.total, 0);
  return (
    <SfCard data-testid="invoicing-milestones">
      <div className="space-y-2.5 border-b border-line-2 px-4 py-3 sm:px-[18px]">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <h2 className="font-heading text-[15px] font-bold text-ink">Payment milestones</h2>
          {sub ? <span className="text-[12.5px] text-ink-3">{sub}</span> : null}
        </div>
        <Segmented
          size="sm"
          ariaLabel="Group payment milestones by"
          value={groupBy}
          onChange={(value) => { setGroupBy(value); setShowAll(false); }}
          testId="milestones-group-by"
          options={MILESTONE_GROUPINGS.map((option) => ({ ...option, testId: `milestones-group-${option.key}` }))}
        />
      </div>
      <ul>{limited.map((item) => <MilestoneRow key={item.key} item={item} />)}</ul>
      {limited.length < items.length ? (
        <div className="border-t border-line-2 px-4 py-2 sm:px-[18px]">
          <LinkButton onClick={() => setShowAll(true)}>Show all {formatNumber(items.length)}</LinkButton>
        </div>
      ) : null}
      <CardFoot className="text-[12px]">
        <span>Total <b className="font-semibold tabular-nums text-ink">{n0(total)}</b></span>
        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <LegendItem dotClass="bg-status-green" label="Received" value="" />
          <LegendItem dotClass="bg-status-amber" label="Awaiting" value="" />
          <LegendItem dotClass="bg-[color:var(--ink-4)]" label="Not started" value="" />
        </span>
      </CardFoot>
    </SfCard>
  );
}

/* ------------------------------------------------------------------ by system type */

const SYSTEM_LIMIT = 5;

/**
 * "By system type" — one table instead of seven repeated blocks: contract value (or, in a filtered view, the
 * same basis as the money bar), invoiced, received, awaiting payment, not started and % collected.
 * props: items (page systemItems: { systemType, projectCount, contract, base, summary }), basis (viewBasis).
 */
export function SystemTable({ items, basis }) {
  const [showAll, setShowAll] = useState(false);
  const mode = basis?.mode || 'contract';
  const baseHeader = { contract: 'Contract value', milestone: 'Scheduled', search: 'Scheduled', period: 'Invoiced in period' }[mode] || 'Contract value';
  const shown = showAll ? items : items.slice(0, SYSTEM_LIMIT);
  const hidden = items.slice(shown.length);
  const hiddenText = hidden.map((item) => `${item.systemType} (${item.summary.awaiting > 0 ? `${n0(item.summary.awaiting)} awaiting` : 'nothing awaiting'})`).join(', ');
  return (
    <SfCard data-testid="invoicing-by-system">
      <CardHead
        title="By system type"
        sub={`${plural(items.length, 'type')} · most awaiting first`}
        right={items.length > SYSTEM_LIMIT ? <LinkButton onClick={() => setShowAll((value) => !value)}>{showAll ? 'Show fewer' : `Show all ${formatNumber(items.length)}`}</LinkButton> : null}
      />
      <TableScroll>
        <table className={cn(tableClass, 'min-w-[860px] table-fixed')}>
          <colgroup>
            <col style={{ width: 150 }} /><col /><col /><col /><col /><col /><col style={{ width: 200 }} />
          </colgroup>
          <thead>
            <tr>
              <th className={thClass}>System</th>
              <th className={cn(thClass, 'text-right')}>{baseHeader}</th>
              <th className={cn(thClass, 'text-right')}>Invoiced</th>
              <th className={cn(thClass, 'text-right')}>Received</th>
              <th className={cn(thClass, 'text-right')}>Awaiting payment</th>
              <th className={cn(thClass, 'text-right')}>Not started</th>
              <th className={thClass}>Collected</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => {
              const bar = moneyBar(item.summary, item.base ?? item.contract);
              const over = mode === 'contract' && item.summary.scheduled - item.contract > 1;
              const pct = bar.base > 0 ? bar.receivedPct : 0;
              return (
                <tr key={item.systemType} className="hover:[&>td]:bg-surface-hover" data-testid={`system-${item.systemType}`}>
                  <td className={tdClass}>
                    <span className="flex min-w-0 items-center gap-2">
                      <SystemChip system={item.systemType} />
                      <span className="truncate text-[12px] text-ink-3">{plural(item.projectCount, 'project')}</span>
                    </span>
                  </td>
                  <td className={cn(tdClass, numClass)}>{n0(bar.base)}</td>
                  <td className={cn(tdClass, numClass)}>{n0(item.summary.invoiced)}</td>
                  <td className={cn(tdClass, numClass)}>{n0(item.summary.received)}</td>
                  <td className={cn(tdClass, numClass, item.summary.awaiting > 0 ? 'font-semibold text-status-amber-ink' : 'text-ink-3')}>{n0(item.summary.awaiting)}</td>
                  <td className={cn(tdClass, numClass, 'py-1.5')}>
                    <span className="block">{n0(item.summary.notStarted)}</span>
                    {over ? <span className="block text-[11px] font-semibold text-status-red-ink" title={`Milestones exceed the contract value by ${n0(item.summary.scheduled - item.contract)}`}>over contract</span> : null}
                  </td>
                  <td className={tdClass}>
                    <span className="flex items-center gap-2.5" data-testid={`system-${item.systemType}-basis`} title={`${formatPercent(pct)} of the ${basis?.rowNoun || 'contract'} value collected`}>
                      <ProgressBar value={pct} tone="green" className="w-[110px] shrink-0" label={`${item.systemType}: collected`} />
                      <span className="text-[12.5px] font-semibold tabular-nums text-ink-2">{bar.base > 0 ? formatPercent(pct) : '—'}</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
      {hidden.length ? (
        <CardFoot>
          <span>Showing {formatNumber(shown.length)} of {formatNumber(items.length)} · {hiddenText} hidden</span>
          <LinkButton onClick={() => setShowAll(true)}>Show all</LinkButton>
        </CardFoot>
      ) : null}
    </SfCard>
  );
}
